import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Save, CheckCircle, AlertCircle, Loader, X, Package, Search, Home, Bed, Utensils, Bath, Briefcase, Car, Box, DoorOpen, Layers, Upload, Camera } from 'lucide-react';
import { adminService } from '../services/adminService';

const CustomerInventoryEditor = ({ token }) => {
  const [roomsData, setRoomsData] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null); // 'success' | 'error' | null
  const [error, setError] = useState(null);
  const [currentRoom, setCurrentRoom] = useState(null);
  const [allExistingItems, setAllExistingItems] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [editedRooms, setEditedRooms] = useState(new Set());

  // Helper function to create correct API URL
  const createApiUrl = (endpoint) => {
    const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
    // Remove trailing slashes from baseUrl
    const cleanBaseUrl = baseUrl.replace(/\/+$/, '');
    // Remove leading slashes from endpoint
    const cleanEndpoint = endpoint.replace(/^\/+/, '');
    
    // Check if baseUrl already contains /api, if so, don't add it again
    if (cleanBaseUrl.endsWith('/api')) {
      // If endpoint starts with 'api/', remove it
      const endpointWithoutApi = cleanEndpoint.replace(/^api\//, '');
      return `${cleanBaseUrl}/${endpointWithoutApi}`;
    }
    
    // Otherwise, add endpoint as is
    return `${cleanBaseUrl}/${cleanEndpoint}`;
  };

  // Load inventory data - supports both deal_id (inspection) and request_token (customer request)
  useEffect(() => {
    const loadInventory = async () => {
      try {
        // Try to load as customer request first (request_token)
        let url = createApiUrl(`api/public/requests/${token}`);
        let response = await fetch(url);
        let isRequest = false;
        
        // If not found, try as inspection (deal_id)
        if (!response.ok) {
          url = createApiUrl(`api/inspections/shared/${token}`);
          response = await fetch(url);
          isRequest = false;
        } else {
          isRequest = true;
        }
        
        if (!response.ok) {
          if (response.status === 404) {
            setError('Link nicht gefunden oder abgelaufen');
          } else if (response.status === 410) {
            setError('Link ist abgelaufen');
          } else {
            setError('Fehler beim Laden der Daten');
          }
          setLoading(false);
          return;
        }

        const data = await response.json();
        console.log('Loaded data:', isRequest ? 'customer request' : 'inspection', data);
        
        // Handle both formats
        let roomsDataObj = {};
        if (isRequest) {
          // Customer request format
          roomsDataObj = data.roomsData && typeof data.roomsData === 'object' ? data.roomsData : {};
        } else {
          // Inspection format
          roomsDataObj = data.roomsData && typeof data.roomsData === 'object' ? data.roomsData : {};
        }
        
        // Load all rooms and items from public endpoints (no auth required)
        let configuredRooms = [];
        let allItems = [];
        try {
          const roomsUrl = createApiUrl('api/public/rooms');
          const roomsResponse = await fetch(roomsUrl);
          if (roomsResponse.ok) {
            configuredRooms = await roomsResponse.json();
            setRooms(configuredRooms.map(r => r.name));
          }
          
          // Load all items from all rooms
          const itemsUrl = createApiUrl('api/public/items');
          const itemsResponse = await fetch(itemsUrl);
          if (itemsResponse.ok) {
            allItems = await itemsResponse.json();
            setAllExistingItems(allItems);
            console.log('Loaded all existing items:', allItems.length);
          } else {
            console.warn('Could not load items from public endpoint');
          }
        } catch (err) {
          console.error('Error loading rooms/items:', err);
        }
        
        // If roomsData is empty, initialize with default rooms and items
        if (!roomsDataObj || Object.keys(roomsDataObj).length === 0) {
          console.log('roomsData is empty, initializing with default rooms and items');
          const defaultRoomsData = {};
          
          // Use configured rooms or fallback to default room names
          const roomNames = configuredRooms.length > 0 
            ? configuredRooms.map(r => r.name)
            : ['Wohnzimmer', 'Schlafzimmer', 'Küche', 'Badezimmer', 'Arbeitszimmer'];
          
          roomNames.forEach(roomName => {
            // Get items for this room from allItems
            const roomItems = allItems.filter(item => item.room === roomName);
            
            defaultRoomsData[roomName] = {
              items: roomItems.length > 0 
                ? roomItems.map(item => ({
                    name: item.name,
                    quantity: 0,
                    volume: item.volume || 0,
                    width: item.width || 0,
                    length: item.length || 0,
                    height: item.height || 0,
                    weight: 0,
                    isPreset: true
                  }))
                : [],
              totalVolume: 0,
              totalWeight: 0
            };
          });
          
          roomsDataObj = defaultRoomsData;
          console.log('Initialized default rooms data:', Object.keys(roomsDataObj));
        }
        
        setRoomsData(roomsDataObj);
        
        // Don't set current room automatically - user will click on tiles
        
        setLoading(false);
      } catch (err) {
        console.error('Error loading inventory:', err);
        setError('Fehler beim Laden der Daten');
        setLoading(false);
      }
    };

    if (token) {
      loadInventory();
    }
  }, [token]);

  // Save inventory data - supports both deal_id and request_token
  const handleSave = useCallback(async () => {
    setSaving(true);
    setSaveStatus(null);
    
    try {
      // Try customer request endpoint first
      let url = createApiUrl(`api/public/requests/${token}`);
      let response = await fetch(url, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ rooms_data: roomsData }),
      });

      // If not found, try inspection endpoint
      if (!response.ok && response.status === 404) {
        url = createApiUrl(`api/inspections/shared/${token}`);
        response = await fetch(url, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ roomsData }),
        });
      }

      if (!response.ok) {
        throw new Error('Fehler beim Speichern');
      }

      setSaveStatus('success');
      setTimeout(() => setSaveStatus(null), 3000);
    } catch (err) {
      console.error('Error saving inventory:', err);
      setSaveStatus('error');
      setTimeout(() => setSaveStatus(null), 3000);
    } finally {
      setSaving(false);
    }
  }, [token, roomsData]);

  // Update room data
  const handleUpdateRoom = useCallback((roomName, updatedData) => {
    setRoomsData(prev => ({
      ...prev,
      [roomName]: {
        ...prev[roomName],
        ...updatedData
      }
    }));
    // Mark room as edited
    setEditedRooms(prev => new Set([...prev, roomName]));
  }, []);

  // Calculate totals
  const totals = useMemo(() => {
    let totalVolume = 0;
    let totalWeight = 0;
    Object.values(roomsData).forEach(roomData => {
      totalVolume += roomData.totalVolume || 0;
      totalWeight += roomData.estimatedWeight || 0;
    });
    return { totalVolume, totalWeight };
  }, [roomsData]);

  // Filter rooms based on search query (must be before early returns)
  const filteredRoomNames = useMemo(() => {
    const allRooms = Object.keys(roomsData);
    if (!searchQuery.trim()) return allRooms;
    const query = searchQuery.toLowerCase();
    return allRooms.filter(roomName => 
      roomName.toLowerCase().includes(query)
    );
  }, [roomsData, searchQuery]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader className="h-8 w-8 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Lade Umzugsgutliste...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Fehler</h2>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  // Get room icon based on room name
  const getRoomIcon = (roomName) => {
    const name = roomName.toLowerCase();
    if (name.includes('wohnzimmer') || name.includes('living')) return <Home className="h-6 w-6" />;
    if (name.includes('schlafzimmer') || name.includes('bedroom')) return <Bed className="h-6 w-6" />;
    if (name.includes('küche') || name.includes('kitchen')) return <Utensils className="h-6 w-6" />;
    if (name.includes('badezimmer') || name.includes('bath')) return <Bath className="h-6 w-6" />;
    if (name.includes('arbeitszimmer') || name.includes('office') || name.includes('study')) return <Briefcase className="h-6 w-6" />;
    if (name.includes('keller') || name.includes('cellar') || name.includes('basement')) return <Box className="h-6 w-6" />;
    if (name.includes('dachboden') || name.includes('attic')) return <Layers className="h-6 w-6" />;
    if (name.includes('flur') || name.includes('hallway') || name.includes('corridor')) return <DoorOpen className="h-6 w-6" />;
    if (name.includes('außen') || name.includes('outdoor') || name.includes('garten')) return <Car className="h-6 w-6" />;
    return <Package className="h-6 w-6" />;
  };

  // Get room color based on volume percentage
  const getRoomColor = (volume, totalVolume) => {
    if (totalVolume === 0) return 'blue';
    const percentage = (volume / totalVolume) * 100;
    if (percentage > 30) return 'blue';
    if (percentage > 15) return 'indigo';
    if (percentage > 5) return 'purple';
    return 'gray';
  };

  const roomNames = Object.keys(roomsData);

  return (
    <div className="min-h-screen bg-gray-50 py-4 sm:py-8">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 mb-4 sm:mb-6">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
            <div className="flex-1">
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Ihre Umzugsgutliste</h1>
              <p className="text-sm sm:text-base text-gray-600 mt-1">Bitte überprüfen und korrigieren Sie Ihre Möbelstücke</p>
            </div>
            <button
              onClick={handleSave}
              disabled={saving}
              className={`w-full sm:w-auto px-4 sm:px-6 py-2 sm:py-3 rounded-lg font-medium flex items-center justify-center gap-2 ${
                saving
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-blue-600 hover:bg-blue-700'
              } text-white transition-colors`}
            >
              {saving ? (
                <>
                  <Loader className="h-5 w-5 animate-spin" />
                  <span className="text-sm sm:text-base">Speichern...</span>
                </>
              ) : (
                <>
                  <Save className="h-5 w-5" />
                  <span className="text-sm sm:text-base">Speichern</span>
                </>
              )}
            </button>
          </div>

          {/* Save Status */}
          {saveStatus && (
            <div className={`mt-4 p-3 rounded-lg flex items-center gap-2 ${
              saveStatus === 'success' 
                ? 'bg-green-50 text-green-800' 
                : 'bg-red-50 text-red-800'
            }`}>
              {saveStatus === 'success' ? (
                <>
                  <CheckCircle className="h-5 w-5" />
                  <span>Erfolgreich gespeichert!</span>
                </>
              ) : (
                <>
                  <AlertCircle className="h-5 w-5" />
                  <span>Fehler beim Speichern. Bitte versuchen Sie es erneut.</span>
                </>
              )}
            </div>
          )}

          {/* Totals - Only Volume */}
          <div className="mt-4 sm:mt-6 flex flex-col sm:flex-row items-stretch sm:items-center gap-4 sm:gap-6">
            <div className="flex-1 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg p-4 border border-blue-100">
              <div className="text-xs sm:text-sm text-gray-600 mb-1">Gesamtvolumen</div>
              <div className="text-xl sm:text-2xl font-bold text-gray-900">
                {totals.totalVolume.toFixed(2)} m³
              </div>
            </div>
            {roomNames.length > 0 && (
              <div className="flex-1 bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg p-4 border border-green-100">
                <div className="text-xs sm:text-sm text-gray-600 mb-1">Bearbeitete Räume</div>
                <div className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">
                  {editedRooms.size} / {roomNames.length}
                </div>
                <div className="w-full sm:w-32 bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-green-500 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${(editedRooms.size / roomNames.length) * 100}%` }}
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Search Bar */}
        {roomNames.length > 0 && (
          <div className="mb-4 sm:mb-6">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 sm:h-5 sm:w-5 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Räume durchsuchen..."
                className="w-full pl-9 sm:pl-10 pr-4 py-2 sm:py-3 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
        )}

        {/* Room Tiles Grid */}
        {filteredRoomNames.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-3 sm:gap-4 mb-4 sm:mb-6">
            {filteredRoomNames.map((roomName) => {
              const roomData = roomsData[roomName];
              const itemCount = roomData?.items?.length || 0;
              const volume = roomData?.totalVolume || 0;
              const volumePercentage = totals.totalVolume > 0 
                ? ((volume / totals.totalVolume) * 100).toFixed(1) 
                : '0.0';
              const isEdited = editedRooms.has(roomName);
              const colorClass = getRoomColor(volume, totals.totalVolume);
              const colorMap = {
                blue: {
                  icon: 'bg-blue-100 text-blue-600 border-blue-200',
                  hover: 'hover:border-blue-300',
                  bar: 'bg-blue-500'
                },
                indigo: {
                  icon: 'bg-indigo-100 text-indigo-600 border-indigo-200',
                  hover: 'hover:border-indigo-300',
                  bar: 'bg-indigo-500'
                },
                purple: {
                  icon: 'bg-purple-100 text-purple-600 border-purple-200',
                  hover: 'hover:border-purple-300',
                  bar: 'bg-purple-500'
                },
                gray: {
                  icon: 'bg-gray-100 text-gray-600 border-gray-200',
                  hover: 'hover:border-gray-300',
                  bar: 'bg-gray-500'
                }
              };
              const colors = colorMap[colorClass];
              
              return (
                <button
                  key={roomName}
                  onClick={() => {
                    setCurrentRoom(roomName);
                    setIsModalOpen(true);
                  }}
                  className={`bg-white rounded-xl shadow-sm p-4 sm:p-6 hover:shadow-lg transition-all duration-200 text-left border-2 border-transparent ${colors.hover} transform hover:-translate-y-1 relative overflow-hidden group active:scale-95`}
                >
                  {/* Edited Badge */}
                  {isEdited && (
                    <div className="absolute top-2 right-2 sm:top-3 sm:right-3 z-10">
                      <div className="bg-green-500 text-white text-xs px-2 py-1 rounded-full flex items-center gap-1 shadow-md">
                        <CheckCircle className="h-3 w-3" />
                        <span className="hidden sm:inline">Bearbeitet</span>
                      </div>
                    </div>
                  )}
                  
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
                      <div className={`${colors.icon} rounded-xl p-2 sm:p-3 group-hover:scale-110 transition-transform duration-200 flex-shrink-0`}>
                        {getRoomIcon(roomName)}
                      </div>
                      <div className="min-w-0 flex-1">
                        <h3 className="text-base sm:text-lg font-semibold text-gray-900 truncate">{roomName}</h3>
                        <p className="text-xs sm:text-sm text-gray-500 mt-1">
                          {itemCount} {itemCount === 1 ? 'Möbelstück' : 'Möbelstücke'}
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="mt-3 sm:mt-4 pt-3 sm:pt-4 border-t border-gray-100">
                    <div className="flex items-end justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="text-xs sm:text-sm text-gray-600">Volumen</div>
                        <div className="text-lg sm:text-xl font-bold text-gray-900 mt-1 truncate">
                          {volume.toFixed(2)} m³
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <div className="text-xs text-gray-500">Anteil</div>
                        <div className="text-base sm:text-lg font-semibold text-gray-700">
                          {volumePercentage}%
                        </div>
                      </div>
                    </div>
                    {/* Volume Bar */}
                    <div className="mt-2 sm:mt-3 w-full bg-gray-200 rounded-full h-1.5 sm:h-2">
                      <div 
                        className={`${colors.bar} h-1.5 sm:h-2 rounded-full transition-all duration-300`}
                        style={{ width: `${Math.min(volumePercentage, 100)}%` }}
                      />
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}

        {filteredRoomNames.length === 0 && roomNames.length > 0 && (
          <div className="bg-white rounded-lg shadow-sm p-6 sm:p-8 text-center">
            <Search className="h-10 w-10 sm:h-12 sm:w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-sm sm:text-base text-gray-600 mb-2">Keine Räume gefunden, die "{searchQuery}" enthalten.</p>
            <button
              onClick={() => setSearchQuery('')}
              className="mt-4 text-blue-600 hover:text-blue-700 text-sm sm:text-base"
            >
              Suche zurücksetzen
            </button>
          </div>
        )}


        {/* Room Editor Modal */}
        {isModalOpen && currentRoom && roomsData[currentRoom] && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-0 sm:p-4 animate-fadeIn"
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                setIsModalOpen(false);
              }
            }}
          >
            <div className="bg-white rounded-none sm:rounded-xl shadow-2xl max-w-4xl w-full h-full sm:h-auto sm:max-h-[90vh] overflow-hidden flex flex-col animate-slideUp">
              {/* Modal Header */}
              <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
                <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
                  <div className="bg-blue-100 rounded-lg p-2 flex-shrink-0">
                    {getRoomIcon(currentRoom)}
                  </div>
                  <h2 className="text-lg sm:text-2xl font-bold text-gray-900 truncate">{currentRoom}</h2>
                </div>
                <button
                  onClick={() => setIsModalOpen(false)}
                  className="p-2 hover:bg-gray-200 rounded-lg transition-colors flex-shrink-0 ml-2"
                  aria-label="Schließen"
                >
                  <X className="h-5 w-5 text-gray-500" />
                </button>
              </div>

              {/* Modal Content */}
              <div className="flex-1 overflow-y-auto">
                <RoomEditor
                  key={currentRoom}
                  roomName={currentRoom}
                  roomData={roomsData[currentRoom]}
                  onUpdateRoom={handleUpdateRoom}
                  allExistingItems={allExistingItems}
                  createApiUrl={createApiUrl}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Room Editor Component
const RoomEditor = ({ roomName, roomData, onUpdateRoom, allExistingItems = [], createApiUrl }) => {
  const [items, setItems] = useState(roomData.items || []);
  const [packMaterials, setPackMaterials] = useState(roomData.packMaterials || []);
  const [notes, setNotes] = useState(roomData.notes || '');
  const [newItem, setNewItem] = useState({ 
    name: '', 
    quantity: 1,
    length: 0, 
    width: 0, 
    height: 0,
    weight: 0,
    demontiert: false,
    remontiert: false,
    duebelarbeiten: false,
    elektro: false
  });
  const [searchResults, setSearchResults] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const prevRoomNameRef = useRef(null);
  
  // AI Analysis states
  const [files, setFiles] = useState([]);
  const [activeInput, setActiveInput] = useState('upload');
  const [stream, setStream] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisError, setAnalysisError] = useState(null);
  const [foundItems, setFoundItems] = useState([]); // Items gefunden durch KI-Analyse
  const videoRef = useRef(null);
  
  // Cleanup function for stream
  useEffect(() => {
    return () => {
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
    };
  }, [stream]);

  // Update items when roomData changes (when switching rooms)
  // Only update if roomName actually changes (not when roomData object reference changes)
  useEffect(() => {
    // Only update if we're switching to a different room (or initial load)
    if (prevRoomNameRef.current !== roomName) {
      setItems(roomData.items || []);
      setPackMaterials(roomData.packMaterials || []);
      setNotes(roomData.notes || '');
      // Clear files when switching rooms
      setFiles(prevFiles => {
        prevFiles.forEach(f => URL.revokeObjectURL(f.preview));
        return [];
      });
      setAnalysisError(null);
      setFoundItems([]); // Clear found items when switching rooms
      prevRoomNameRef.current = roomName;
    }
  }, [roomName, roomData]);

  // Recalculate totals when items change
  useEffect(() => {
    const totalVolume = items.reduce((total, item) => {
      const itemVolume = (item.length * item.width * item.height) / 1000000; // cm³ to m³
      return total + itemVolume * item.quantity;
    }, 0);

    const estimatedWeight = items.reduce((total, item) => {
      if (item.weight) {
        return total + (item.weight * item.quantity);
      }
      const itemVolume = (item.length * item.width * item.height) / 1000000;
      return total + (itemVolume * 200 * item.quantity); // 200 kg/m³ default
    }, 0);

    onUpdateRoom(roomName, {
      items,
      packMaterials,
      notes,
      totalVolume,
      estimatedWeight
    });
  }, [items, packMaterials, notes, roomName, onUpdateRoom]);

  const handleQuantityChange = (index, change) => {
    setItems(prevItems => {
      const newItems = [...prevItems];
      newItems[index] = {
        ...newItems[index],
        quantity: Math.max(0, newItems[index].quantity + change)
      };
      return newItems;
    });
  };

  const handleItemUpdate = (index, field, value) => {
    setItems(prevItems => {
      const newItems = [...prevItems];
      newItems[index] = {
        ...newItems[index],
        [field]: value
      };
      return newItems;
    });
  };

  const handleRemoveItem = (index) => {
    setItems(prevItems => prevItems.filter((_, i) => i !== index));
  };

  // Filter suggestions based on input
  const filterSuggestions = (input) => {
    if (!input) {
      setSearchResults([]);
      return;
    }
    
    const filteredItems = allExistingItems.filter(item => 
      item.name.toLowerCase().startsWith(input.toLowerCase()) &&
      !items.some(existingItem => existingItem.name === item.name)
    );
    
    setSearchResults(filteredItems);
    setShowSuggestions(true);
  };

  // Handle item selection from suggestions
  const handleSelectSuggestion = (suggestion) => {
    setNewItem({
      name: suggestion.name,
      quantity: 1,
      length: parseFloat(suggestion.length) || 0,
      width: parseFloat(suggestion.width) || 0,
      height: parseFloat(suggestion.height) || 0,
      weight: parseFloat(suggestion.weight) || 0,
      setupTime: parseInt(suggestion.setupTime) || 0,
      dismantleTime: parseInt(suggestion.dismantleTime) || 0,
      demontiert: false,
      remontiert: false,
      duebelarbeiten: false,
      elektro: false
    });
    setShowSuggestions(false);
  };

  const handleAddItem = () => {
    if (newItem.name && !items.some(item => item.name === newItem.name)) {
      setItems(prevItems => [...prevItems, { ...newItem, quantity: 1 }]);
      // Reset newItem
      setNewItem({ 
        name: '', 
        quantity: 1,
        length: 0, 
        width: 0, 
        height: 0,
        weight: 0,
        demontiert: false,
        remontiert: false,
        duebelarbeiten: false,
        elektro: false
      });
      setShowSuggestions(false);
    }
  };

  // Check if item is preset (exists in allExistingItems)
  const isPresetItem = useCallback((item) => {
    return allExistingItems.some(existingItem => 
      existingItem.name.toLowerCase() === item.name?.toLowerCase()
    );
  }, [allExistingItems]);

  // Handle found item update (editing)
  const handleFoundItemUpdate = (itemId, updates) => {
    setFoundItems(prevItems => 
      prevItems.map(item => 
        item.id === itemId ? { ...item, ...updates } : item
      )
    );
  };

  // Handle confirm found item (add to list)
  const handleConfirmFoundItem = (itemToAdd) => {
    // Check if item with same name already exists
    const existingNames = new Set(items.map(i => i.name.toLowerCase()));
    if (existingNames.has(itemToAdd.name.toLowerCase())) {
      setAnalysisError(`Ein Item mit dem Namen "${itemToAdd.name}" existiert bereits in der Liste.`);
      return;
    }
    
    // Add to items list
    setItems(prevItems => [...prevItems, { ...itemToAdd }]);
    
    // Remove from found items
    setFoundItems(prevItems => prevItems.filter(item => item.id !== itemToAdd.id));
    
    setAnalysisError(null);
  };

  // Handle remove found item
  const handleRemoveFoundItem = (itemId) => {
    setFoundItems(prevItems => prevItems.filter(item => item.id !== itemId));
  };

  // File upload handler
  const handleFileUpload = (event) => {
    try {
      const selectedFiles = Array.from(event.target.files).filter(file => {
        const isValidType = file.type.startsWith('image/');
        const isValidSize = file.size <= 10 * 1024 * 1024; // 10MB for images
        return isValidType && isValidSize;
      });

      if (selectedFiles.length === 0) {
        setAnalysisError('Bitte nur Bilder bis 10MB auswählen');
        return;
      }

      setFiles(prev => [
        ...prev,
        ...selectedFiles.map(file => ({
          id: Date.now() + Math.random(),
          file,
          preview: URL.createObjectURL(file),
          type: 'photo'
        }))
      ]);
      setAnalysisError(null);
    } catch (err) {
      setAnalysisError('Fehler beim Laden der Dateien');
    }
  };

  // Camera capture handler
  const handleCameraCapture = async () => {
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'environment',
          width: { ideal: 1920 },
          height: { ideal: 1080 }
        }
      });
      
      setStream(mediaStream);
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
      }
      setActiveInput('camera');
      setAnalysisError(null);
    } catch (err) {
      setAnalysisError('Kamera konnte nicht gestartet werden');
    }
  };

  // Capture photo from camera
  const handleCapture = async () => {
    if (!stream || !videoRef.current) return;

    try {
      const videoTrack = stream.getVideoTracks()[0];
      const imageCapture = new ImageCapture(videoTrack);
      const blob = await imageCapture.takePhoto();
      const file = new File([blob], `${roomName}-${Date.now()}.jpg`, { type: 'image/jpeg' });
      
      setFiles(prev => [...prev, {
        id: Date.now(),
        file,
        preview: URL.createObjectURL(blob)
      }]);

      stopCamera();
      setAnalysisError(null);
    } catch (err) {
      setAnalysisError('Fehler beim Aufnehmen des Fotos');
    }
  };

  // Stop camera
  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
    setActiveInput('upload');
  };

  // Remove file
  const removeFile = (fileId) => {
    setFiles(prev => {
      const newFiles = prev.filter(f => f.id !== fileId);
      prev
        .filter(f => f.id === fileId)
        .forEach(f => URL.revokeObjectURL(f.preview));
      return newFiles;
    });
  };

  // Clear all files
  const clearAllFiles = () => {
    files.forEach(f => URL.revokeObjectURL(f.preview));
    setFiles([]);
    setAnalysisError(null);
  };

  // Analyze images with AI
  const handleAnalyze = async () => {
    let mediaData = [];
    setIsAnalyzing(true);
    setAnalysisError(null);
    
    try {
      if (files.length === 0) {
        throw new Error('Bitte wählen Sie mindestens ein Bild aus.');
      }

      const mediaPromises = files.map(async (file) => {
        return new Promise((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve({
            type: file.type === 'photo' ? 'image' : 'video',
            data: reader.result,
            size: file.file.size
          });
          reader.readAsDataURL(file.file);
        });
      });
  
      mediaData = await Promise.all(mediaPromises);
      
      const customPrompt = `Analysiere diese Bilder eines ${roomName}s und identifiziere alle Möbelstücke. 
Für jeden Gegenstand gib an:
1. Name auf Deutsch
2. Geschätzte Maße (L x B x H in cm) als String im Format "Länge x Breite x Höhe"
3. Geschätztes Volumen in m³
4. Kurze Beschreibung (Material, Farbe, Zustand)

Berechne auch das Gesamtvolumen aller Möbel.
Gib auch Umzugshinweise und Besonderheiten an.

Das Format der Maße muss in cm sein (also bei 1m = 100cm), aber das Volumen in m³.
Format als JSON:
{
  "items": [{
    "name": string,
    "dimensions": string,    // Format: "200 x 100 x 75" (in cm)
    "volume": number,       // in m³
    "description": string
  }],
  "totalVolume": number,   // Gesamtvolumen in m³
  "summary": string,       // Zusammenfassung des Raums
  "movingTips": string    // Umzugshinweise
}`;

      const requestData = {
        images: mediaData.filter(m => m.type === 'image').map(m => m.data),
        videos: mediaData.filter(m => m.type === 'video').map(m => m.data),
        roomName,
        customPrompt: customPrompt || ''
      };

      const apiUrl = createApiUrl ? createApiUrl('api/analyze') : `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/analyze`;
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(requestData)
      });

      let errorData;
      const responseText = await response.text();
      
      try {
        errorData = JSON.parse(responseText);
      } catch (e) {
        errorData = { error: responseText || 'Keine Details verfügbar' };
      }

      if (!response.ok) {
        let errorMessage;
        switch (response.status) {
          case 500:
            console.error('Server error details:', errorData);
            if (errorData.details?.includes('db is not defined')) {
              errorMessage = 'Datenbankverbindung fehlgeschlagen. Bitte kontaktieren Sie den Support.';
            } else if (errorData.details?.includes('Failed to parse AI response')) {
              errorMessage = 'Die KI-Analyse konnte nicht verarbeitet werden. Bitte versuchen Sie es mit einem anderen Bild.';
            } else if (errorData.details) {
              errorMessage = 'Server-Fehler: ' + errorData.details;
            } else if (errorData.error) {
              errorMessage = 'Server-Fehler: ' + errorData.error;
            } else {
              errorMessage = 'Server-Fehler: Bitte versuchen Sie es später erneut.';
            }
            break;
          case 413:
            errorMessage = 'Die Bilder sind zu groß. Bitte verwenden Sie kleinere Dateien.';
            break;
          case 401:
            errorMessage = 'Authentifizierungsfehler. Bitte melden Sie sich erneut an.';
            break;
          default:
            errorMessage = errorData.error || errorData.details || `Ein unerwarteter Fehler ist aufgetreten (Status: ${response.status})`;
        }
        throw new Error(errorMessage);
      }

      if (!errorData || typeof errorData !== 'object') {
        throw new Error('Ungültiges Antwortformat vom Server');
      }

      if (!Array.isArray(errorData.items)) {
        throw new Error('Keine Gegenstände in der Analyse gefunden');
      }

      // Convert analysis results to items format - don't add to list yet
      // Support both formats: dimensions (string) or length/width/height (numbers)
      const newItems = errorData.items.map((item, index) => {
        let length = 0, width = 0, height = 0;
        
        // If dimensions is a string like "200 x 100 x 75", parse it
        if (item.dimensions && typeof item.dimensions === 'string') {
          const matches = item.dimensions.match(/(\d+)\s*x\s*(\d+)\s*x\s*(\d+)/i);
          if (matches) {
            length = parseInt(matches[1]) || 0;
            width = parseInt(matches[2]) || 0;
            height = parseInt(matches[3]) || 0;
          }
        } else {
          // Use direct length/width/height if provided
          length = item.length || 0;
          width = item.width || 0;
          height = item.height || 0;
        }
        
        return {
          id: `found-${Date.now()}-${index}`, // Unique ID for found items
          name: item.name,
          quantity: item.count || 1,
          length: length,
          width: width,
          height: height,
          weight: 0, // AI doesn't provide weight, can be calculated or set manually later
          description: item.description || '',
          demontiert: false,
          remontiert: false,
          duebelarbeiten: false,
          elektro: false
        };
      });

      // Store found items for review - don't add to list automatically
      setFoundItems(newItems);

      setAnalysisError(null);
      
      // Clear files after successful analysis
      clearAllFiles();
      
    } catch (err) {
      setAnalysisError(err.message || 'Fehler bei der Analyse. Bitte versuchen Sie es später erneut.');
    } finally {
      setIsAnalyzing(false);
    }
  };

  return (
    <div className="p-4 sm:p-6">
      {/* Items List */}
      <div className="space-y-3 mb-4 sm:mb-6">
        {items.map((item, index) => {
          const isPreset = isPresetItem(item);
          
          return (
            <div key={index} className="bg-gray-50 rounded-lg p-3 sm:p-4 border border-gray-200">
              <div className="grid grid-cols-1 sm:grid-cols-12 gap-3 sm:gap-4 items-start sm:items-center">
                {/* Item Name */}
                <div className="sm:col-span-3">
                  <label className="block text-xs text-gray-500 mb-1 sm:hidden">Möbelstück</label>
                  <input
                    type="text"
                    value={item.name || ''}
                    onChange={(e) => handleItemUpdate(index, 'name', e.target.value)}
                    placeholder="Möbelstück"
                    disabled={isPreset}
                    className={`w-full p-2 text-sm sm:text-base border border-gray-300 rounded-md ${
                      isPreset ? 'bg-gray-100 cursor-not-allowed' : ''
                    }`}
                    title={isPreset ? 'Voreingestelltes Item - Name nicht bearbeitbar' : ''}
                  />
                </div>

                {/* Dimensions */}
                <div className="sm:col-span-6 grid grid-cols-3 gap-2">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1 sm:hidden">Länge</label>
                    <input
                      type="number"
                      value={item.length || ''}
                      onChange={(e) => handleItemUpdate(index, 'length', parseFloat(e.target.value) || 0)}
                      placeholder="L (cm)"
                      disabled={isPreset}
                      className={`w-full p-2 border border-gray-300 rounded-md text-sm ${
                        isPreset ? 'bg-gray-100 cursor-not-allowed' : ''
                      }`}
                      title={isPreset ? 'Voreingestelltes Item - Größe nicht bearbeitbar' : ''}
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1 sm:hidden">Breite</label>
                    <input
                      type="number"
                      value={item.width || ''}
                      onChange={(e) => handleItemUpdate(index, 'width', parseFloat(e.target.value) || 0)}
                      placeholder="B (cm)"
                      disabled={isPreset}
                      className={`w-full p-2 border border-gray-300 rounded-md text-sm ${
                        isPreset ? 'bg-gray-100 cursor-not-allowed' : ''
                      }`}
                      title={isPreset ? 'Voreingestelltes Item - Größe nicht bearbeitbar' : ''}
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1 sm:hidden">Höhe</label>
                    <input
                      type="number"
                      value={item.height || ''}
                      onChange={(e) => handleItemUpdate(index, 'height', parseFloat(e.target.value) || 0)}
                      placeholder="H (cm)"
                      disabled={isPreset}
                      className={`w-full p-2 border border-gray-300 rounded-md text-sm ${
                        isPreset ? 'bg-gray-100 cursor-not-allowed' : ''
                      }`}
                      title={isPreset ? 'Voreingestelltes Item - Größe nicht bearbeitbar' : ''}
                    />
                  </div>
                </div>

              {/* Quantity */}
              <div className="sm:col-span-2 flex items-center gap-2 justify-between sm:justify-start">
                <label className="block text-xs text-gray-500 sm:hidden">Menge</label>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleQuantityChange(index, -1)}
                    className="w-8 h-8 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center hover:bg-blue-200 transition-colors"
                    aria-label="Menge reduzieren"
                  >
                    -
                  </button>
                  <span className="w-12 text-center font-medium text-sm sm:text-base">{item.quantity || 0}</span>
                  <button
                    onClick={() => handleQuantityChange(index, 1)}
                    className="w-8 h-8 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center hover:bg-blue-200 transition-colors"
                    aria-label="Menge erhöhen"
                  >
                    +
                  </button>
                </div>
              </div>

              {/* Remove Button */}
              <div className="sm:col-span-1 flex justify-end sm:justify-start">
                <button
                  onClick={() => handleRemoveItem(index)}
                  className="w-full sm:w-auto px-3 py-1.5 sm:py-1 text-red-600 hover:bg-red-50 rounded-md text-sm transition-colors"
                >
                  Entfernen
                </button>
              </div>
            </div>

            {/* Service Tags */}
            {(item.demontiert || item.remontiert || item.duebelarbeiten || item.elektro) && (
              <div className="flex gap-2 mt-2">
                {item.demontiert && <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded">Demontiert</span>}
                {item.remontiert && <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded">Remontiert</span>}
                {item.duebelarbeiten && <span className="px-2 py-1 bg-orange-100 text-orange-800 text-xs rounded">Dübelarbeiten</span>}
                {item.elektro && <span className="px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded">Elektro</span>}
              </div>
            )}
          </div>
          );
        })}

        {items.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            Keine Möbelstücke vorhanden
          </div>
        )}
      </div>

      {/* AI Analysis Section */}
      <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-blue-50 rounded-lg border border-blue-200">
        <h4 className="text-sm sm:text-base font-semibold text-gray-700 mb-3 flex items-center gap-2">
          <Camera className="h-4 w-4" />
          Raum mit KI analysieren
        </h4>
        <p className="text-xs sm:text-sm text-gray-600 mb-3">
          Machen Sie Fotos von Ihrem Raum und lassen Sie Möbelstücke automatisch erkennen
        </p>

        {/* Error Display */}
        {analysisError && (
          <div className="mb-3 bg-red-50 border border-red-200 text-red-600 p-3 rounded-lg flex items-center">
            <X className="w-4 h-4 mr-2 flex-shrink-0" />
            <span className="text-xs sm:text-sm">{analysisError}</span>
          </div>
        )}

        {/* Camera View */}
        {activeInput === 'camera' && stream && (
          <div className="mb-3 relative rounded-lg overflow-hidden">
            <video
              ref={videoRef}
              autoPlay
              playsInline
              className="w-full h-[300px] object-cover"
            />
            <div className="absolute bottom-4 left-0 right-0 flex justify-center space-x-4">
              <button
                onClick={handleCapture}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 text-sm"
              >
                Foto aufnehmen
              </button>
              <button
                onClick={stopCamera}
                className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 text-sm"
              >
                Abbrechen
              </button>
            </div>
          </div>
        )}

        {/* Upload Area */}
        {activeInput === 'upload' && files.length === 0 && (
          <div className="mb-3 border-2 border-dashed border-gray-300 rounded-lg p-6">
            <input
              type="file"
              accept="image/*"
              multiple
              onChange={handleFileUpload}
              className="hidden"
              id={`file-upload-${roomName}`}
            />
            <label
              htmlFor={`file-upload-${roomName}`}
              className="cursor-pointer block text-center"
            >
              <Upload className="mx-auto h-8 w-8 text-gray-400" />
              <p className="mt-2 text-sm text-gray-600">
                Klicken zum Hochladen oder Bilder hierher ziehen
              </p>
              <p className="mt-1 text-xs text-gray-500">
                Maximal 10MB pro Bild
              </p>
            </label>
            <div className="mt-3 flex justify-center">
              <button
                onClick={handleCameraCapture}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm flex items-center gap-2"
              >
                <Camera className="w-4 h-4" />
                Kamera öffnen
              </button>
            </div>
          </div>
        )}

        {/* Preview Area */}
        {files.length > 0 && (
          <div className="mb-3 space-y-2">
            <div className="flex items-center justify-between">
              <h5 className="text-sm font-medium text-gray-700">
                Ausgewählte Bilder ({files.length})
              </h5>
              <button
                onClick={clearAllFiles}
                className="text-red-500 text-xs hover:text-red-700"
              >
                Alle löschen
              </button>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {files.map(file => (
                <div key={file.id} className="relative group min-h-[100px] max-h-[150px] w-full">
                  <img
                    src={file.preview}
                    alt="Vorschau"
                    className="w-full h-full object-cover bg-gray-100 rounded-lg"
                  />
                  <button
                    onClick={() => removeFile(file.id)}
                    className="absolute top-1 right-1 p-1 bg-black bg-opacity-50 text-white rounded-full hover:bg-opacity-75 transition-colors"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleCameraCapture}
                className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm flex items-center gap-2"
              >
                <Camera className="w-4 h-4" />
                Weitere Fotos
              </button>
            </div>
          </div>
        )}

        {/* Analysis Button */}
        <button
          disabled={isAnalyzing || files.length === 0}
          className="w-full bg-blue-600 text-white py-2.5 rounded-lg disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-sm sm:text-base"
          onClick={handleAnalyze}
        >
          {isAnalyzing ? (
            <>
              <Loader className="w-4 h-4 animate-spin" />
              Analysiere mit KI...
            </>
          ) : (
            <>
              <Camera className="w-4 h-4" />
              KI-Analyse starten
            </>
          )}
        </button>
      </div>

      {/* Found Items Section */}
      {foundItems.length > 0 && (
        <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-green-50 rounded-lg border border-green-200">
          <h4 className="text-sm sm:text-base font-semibold text-gray-700 mb-3 flex items-center gap-2">
            <CheckCircle className="h-4 w-4 text-green-600" />
            Gefundene Möbelstücke ({foundItems.length})
          </h4>
          <p className="text-xs sm:text-sm text-gray-600 mb-3">
            Überprüfen Sie die gefundenen Items, bearbeiten Sie sie bei Bedarf und fügen Sie sie zur Liste hinzu
          </p>
          
          <div className="space-y-3">
            {foundItems.map((item, index) => (
              <div key={item.id} className="bg-white rounded-lg p-3 sm:p-4 border border-gray-200 shadow-sm">
                <div className="grid grid-cols-1 sm:grid-cols-12 gap-3 sm:gap-4 items-start sm:items-center mb-3">
                  {/* Item Name */}
                  <div className="sm:col-span-3">
                    <label className="block text-xs text-gray-500 mb-1 sm:hidden">Name</label>
                    <input
                      type="text"
                      value={item.name || ''}
                      onChange={(e) => handleFoundItemUpdate(item.id, { name: e.target.value })}
                      placeholder="Möbelstück"
                      className="w-full p-2 text-sm sm:text-base border border-gray-300 rounded-md"
                    />
                  </div>

                  {/* Dimensions */}
                  <div className="sm:col-span-6 grid grid-cols-3 gap-2">
                    <div>
                      <label className="block text-xs text-gray-500 mb-1 sm:hidden">Länge</label>
                      <input
                        type="number"
                        value={item.length || ''}
                        onChange={(e) => handleFoundItemUpdate(item.id, { length: parseFloat(e.target.value) || 0 })}
                        placeholder="L (cm)"
                        className="w-full p-2 border border-gray-300 rounded-md text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1 sm:hidden">Breite</label>
                      <input
                        type="number"
                        value={item.width || ''}
                        onChange={(e) => handleFoundItemUpdate(item.id, { width: parseFloat(e.target.value) || 0 })}
                        placeholder="B (cm)"
                        className="w-full p-2 border border-gray-300 rounded-md text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1 sm:hidden">Höhe</label>
                      <input
                        type="number"
                        value={item.height || ''}
                        onChange={(e) => handleFoundItemUpdate(item.id, { height: parseFloat(e.target.value) || 0 })}
                        placeholder="H (cm)"
                        className="w-full p-2 border border-gray-300 rounded-md text-sm"
                      />
                    </div>
                  </div>

                  {/* Quantity */}
                  <div className="sm:col-span-2 flex items-center gap-2 justify-between sm:justify-start">
                    <label className="block text-xs text-gray-500 sm:hidden">Menge</label>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleFoundItemUpdate(item.id, { quantity: Math.max(1, (item.quantity || 1) - 1) })}
                        className="w-8 h-8 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center hover:bg-blue-200 transition-colors"
                        aria-label="Menge reduzieren"
                      >
                        -
                      </button>
                      <span className="w-12 text-center font-medium text-sm sm:text-base">{item.quantity || 1}</span>
                      <button
                        onClick={() => handleFoundItemUpdate(item.id, { quantity: (item.quantity || 1) + 1 })}
                        className="w-8 h-8 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center hover:bg-blue-200 transition-colors"
                        aria-label="Menge erhöhen"
                      >
                        +
                      </button>
                    </div>
                  </div>

                  {/* Remove Button */}
                  <div className="sm:col-span-1 flex justify-end sm:justify-start">
                    <button
                      onClick={() => handleRemoveFoundItem(item.id)}
                      className="w-full sm:w-auto px-3 py-1.5 sm:py-1 text-red-600 hover:bg-red-50 rounded-md text-sm transition-colors"
                    >
                      Entfernen
                    </button>
                  </div>
                </div>

                {/* Description */}
                <div className="mb-3">
                  <label className="block text-xs text-gray-500 mb-1">Beschreibung</label>
                  <textarea
                    value={item.description || ''}
                    onChange={(e) => handleFoundItemUpdate(item.id, { description: e.target.value })}
                    placeholder="Beschreibung (optional)"
                    className="w-full p-2 text-sm border border-gray-300 rounded-md"
                    rows="2"
                  />
                </div>

                {/* Confirm Button */}
                <div className="flex justify-end gap-2 mt-3 pt-3 border-t border-gray-200">
                  <button
                    onClick={() => handleConfirmFoundItem(item)}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm sm:text-base flex items-center gap-2"
                  >
                    <CheckCircle className="w-4 h-4" />
                    Zur Liste hinzufügen
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Add Item Form */}
      <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-gray-50 rounded-lg border border-gray-200">
        <h4 className="text-sm sm:text-base font-semibold text-gray-700 mb-3">Neues Möbelstück hinzufügen</h4>
        <div className="space-y-3">
          <div className="relative">
            <input
              type="text"
              value={newItem.name}
              onChange={(e) => {
                setNewItem({...newItem, name: e.target.value});
                filterSuggestions(e.target.value);
              }}
              onFocus={() => {
                if (newItem.name) {
                  filterSuggestions(newItem.name);
                }
              }}
              placeholder="Möbelstück Name (z.B. 'Sofa')"
              className="w-full p-2 text-sm sm:text-base border border-gray-300 rounded-md"
            />
            {showSuggestions && searchResults.length > 0 && (
              <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-48 sm:max-h-60 overflow-auto">
                {searchResults.map((suggestion, index) => (
                  <div
                    key={index}
                    className="p-2 hover:bg-gray-100 cursor-pointer flex flex-col sm:flex-row sm:justify-between sm:items-center gap-1 sm:gap-0"
                    onClick={() => handleSelectSuggestion(suggestion)}
                  >
                    <span className="text-sm sm:text-base">{suggestion.name}</span>
                    <span className="text-xs sm:text-sm text-gray-500">
                      {suggestion.length}×{suggestion.width}×{suggestion.height}cm
                      {suggestion.weight ? ` • ${suggestion.weight}kg` : ''}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
          
          <div className="grid grid-cols-3 gap-2">
            <input
              type="number"
              value={newItem.length || ''}
              onChange={(e) => setNewItem({...newItem, length: parseFloat(e.target.value) || 0})}
              placeholder="Länge (cm)"
              className="p-2 border border-gray-300 rounded-md text-sm"
            />
            <input
              type="number"
              value={newItem.width || ''}
              onChange={(e) => setNewItem({...newItem, width: parseFloat(e.target.value) || 0})}
              placeholder="Breite (cm)"
              className="p-2 border border-gray-300 rounded-md text-sm"
            />
            <input
              type="number"
              value={newItem.height || ''}
              onChange={(e) => setNewItem({...newItem, height: parseFloat(e.target.value) || 0})}
              placeholder="Höhe (cm)"
              className="p-2 border border-gray-300 rounded-md text-sm"
            />
          </div>
          
          <button
            onClick={handleAddItem}
            disabled={!newItem.name}
            className="w-full px-4 py-2.5 sm:py-2 bg-green-500 text-white rounded-md hover:bg-green-600 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed text-sm sm:text-base"
          >
            + Möbelstück hinzufügen
          </button>
        </div>
      </div>

      {/* Notes */}
      <div className="mt-4 sm:mt-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">Notizen</label>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Zusätzliche Informationen für diesen Raum..."
          className="w-full p-2 text-sm sm:text-base border border-gray-300 rounded-md min-h-[80px] sm:min-h-[100px]"
        />
      </div>

      {/* Room Totals - Only Volume */}
      <div className="mt-4 sm:mt-6">
        <div className="bg-gray-50 rounded-lg p-3 sm:p-4 inline-block w-full sm:w-auto">
          <div className="text-xs sm:text-sm text-gray-600 mb-1">Volumen</div>
          <div className="text-lg sm:text-xl font-bold text-gray-900">
            {((roomData.totalVolume || 0).toFixed(2))} m³
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerInventoryEditor;

